import { MarkdownViewer } from '@/components/MarkdownViewer'
import { ScrollableContent } from '@/components/ScrollableContent'
import { EntityHistoryPanel } from '@/components/history'
import { MotionPlayIcon } from '@/components/icons'
import { TriggerWorkflowDialog } from '@/components/projects/workflows/TriggerWorkflowDialog'
import { TriggerWorkflowFromEntityDialog, type TriggerableDefinition } from '@/components/projects/workflows/TriggerWorkflowFromEntityDialog'
import { WorkflowHistoryTab } from '@/components/projects/workflows/WorkflowHistoryTab'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { CopyButton } from '@/components/ui/copy-button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { TabBar } from '@/components/TabBar'
import { useAuth } from '@/hooks'
import { useTestCases } from '@/hooks/useTestCases'
import { useWorkItemAttachments } from '@/hooks/useWorkItemAttachments'
import { Permissions } from '@/lib/permissions'
import { EntityHistoryEntityTypes, type WorkItem } from '@/types'
import { History, Pencil, Plus } from 'lucide-react'
import { useState, useRef, useCallback, useEffect } from 'react'
import { AttachmentsTab } from './AttachmentsTab'
import { TestCasesTab } from './TestCasesTab'
import { WORK_ITEM_HISTORY_FIELD_LABELS } from './workItemHistoryFields'

const FIELD_MAX_HEIGHT = 'calc(1.625em * 20 + 2rem)'

type Tab = 'overview' | 'workflow-history' | 'test-cases' | 'attachments'

const baseTabs: { key: Tab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'workflow-history', label: 'Workflow History' },
  { key: 'test-cases', label: 'Test Cases' },
  { key: 'attachments', label: 'Attachments' },
]

interface WorkItemDetailDialogProps {
  workItem: WorkItem | null
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onEdit?: () => void
}

export function WorkItemDetailDialog({ workItem, projectId, open, onOpenChange, onEdit }: WorkItemDetailDialogProps) {
  const { hasPermission } = useAuth(projectId)
  const canEdit = hasPermission(Permissions.Project.WorkItem.Update, projectId)
  const canViewHistory = hasPermission(Permissions.Project.WorkItem.Read, projectId)
  const canTrigger = hasPermission(Permissions.Project.Workflow.Trigger, projectId)
  const [activeTab, setActiveTab] = useState<Tab>('overview')
  const [showHistory, setShowHistory] = useState(false)
  const [triggerPickerOpen, setTriggerPickerOpen] = useState(false)
  const [triggerDefinition, setTriggerDefinition] = useState<TriggerableDefinition | null>(null)

  const activeItemKey = open ? workItem?.id ?? null : null
  const [prevItemKey, setPrevItemKey] = useState<string | null>(null)
  if (activeItemKey !== prevItemKey) {
    setPrevItemKey(activeItemKey)
    if (activeItemKey) {
      setActiveTab('overview')
      setShowHistory(false)
    }
  }

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab)
    setShowHistory(false)
  }

  const { data: testCasesData } = useTestCases(
    projectId,
    workItem?.id ?? '',
  )

  const { data: attachmentsData } = useWorkItemAttachments(
    projectId,
    workItem?.id ?? '',
  )

  const descriptionMarkdownRef = useRef<HTMLDivElement>(null)
  const technicalApproachMarkdownRef = useRef<HTMLDivElement>(null)

  const historyButtonRef = useRef<HTMLButtonElement>(null)
  const historyWasOpen = useRef(false)

  // The button unmounts while history is open, so closing has to hand focus back to it once it
  // remounts. Guarded on the previous state so the effect does not steal focus on first render.
  useEffect(() => {
    if (historyWasOpen.current && !showHistory)
      historyButtonRef.current?.focus()

    historyWasOpen.current = showHistory
  }, [showHistory])
  const getDescriptionHtml = useCallback(() => descriptionMarkdownRef.current?.innerHTML, [])
  const getTechnicalApproachHtml = useCallback(() => technicalApproachMarkdownRef.current?.innerHTML, [])

  if (!workItem) return null

  const testCaseCount = testCasesData?.totalCount ?? 0
  const attachmentCount = attachmentsData?.totalCount ?? 0

  const tabs = baseTabs.map(t => ({
    ...t,
    badge: t.key === 'test-cases'
      ? testCaseCount
      : t.key === 'attachments'
        ? attachmentCount
        : undefined,
  }))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[100vw] w-[100vw] h-[100vh] max-h-[100vh] flex flex-col p-0 rounded-none border-none gap-0">
        <DialogHeader className="px-8 py-4 border-b shrink-0 bg-background">
          <DialogTitle className="text-xl font-bold">
            Work Item #{String(workItem.number).padStart(3, '0')} - {workItem.title}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto bg-input-background">
          <div className="sticky top-0 z-10 bg-input-background">
            <div className="max-w-[1100px] w-full mx-auto px-6">
              <div className="flex items-center justify-between py-3 px-2">
                <TabBar tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} className="bg-background" />

                {activeTab === 'overview' && (
                  <div className="flex items-center gap-2">
                    {canViewHistory && !showHistory && (
                      <Button
                        ref={historyButtonRef}
                        type="button"
                        variant="ghost"
                        size="iconLg"
                        className="hover:shadow-sm"
                        onClick={() => setShowHistory(true)}
                        aria-label="View history"
                        data-testid="work-item-history-button"
                      >
                        <History className="h-4 w-4" />
                      </Button>
                    )}
                    {onEdit && canEdit && !showHistory && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="iconLg"
                        className="hover:shadow-sm"
                        onClick={onEdit}
                        aria-label="Edit work item"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                )}

                {activeTab === 'attachments' && canEdit && (
                  <Button
                    type="button"
                    onClick={() => {
                      const dropZone = document.querySelector<HTMLElement>('[data-testid="attachments-drop-zone"]')
                      dropZone?.click()
                    }}
                    aria-label="Add attachments"
                    data-testid="add-attachments-button"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    New Attachments
                  </Button>
                )}

                {activeTab === 'workflow-history' && canTrigger && (
                  <Button
                    type="button"
                    onClick={() => setTriggerPickerOpen(true)}
                    aria-label="Trigger workflow"
                    data-testid="trigger-workflow-from-history-button"
                  >
                    <MotionPlayIcon className="h-4 w-4 mr-1" />
                    Trigger
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="max-w-[1100px] mx-auto px-6">
              {activeTab === 'overview' && showHistory && (
                <EntityHistoryPanel
                  projectId={projectId}
                  entityType={EntityHistoryEntityTypes.WorkItem}
                  entityId={workItem.id}
                  entityLabel="Work Item"
                  fieldLabels={WORK_ITEM_HISTORY_FIELD_LABELS}
                  onClose={() => setShowHistory(false)}
                />
              )}

              {activeTab === 'overview' && !showHistory && (
                <>
                  <Card className="p-8 mb-8 overflow-hidden break-words">
                    <h2 className="text-2xl font-medium mb-6 text-foreground">Overview</h2>

                    <div className="grid grid-cols-3 gap-6 mb-8">
                      {workItem.epic && (
                        <div>
                          <p className="text-xs font-semibold">Epic</p>
                          <p className="text-sm text-muted-foreground">{workItem.epic}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-xs font-semibold">Status</p>
                        <p className="text-sm text-muted-foreground">{workItem.status}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold">Created</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(workItem.createdAt).toLocaleDateString('sv-SE')}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold">Assigned to</p>
                        <p className="text-sm text-muted-foreground" data-testid="work-item-detail-assignee">
                          {workItem.assignedToDisplayName || workItem.assignedToEmail || 'Unassigned'}
                        </p>
                      </div>
                    </div>

                    <hr className="mb-8" />

                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-2xl font-medium text-foreground">Description</h2>
                      {!!workItem.description?.trim() && (
                        <CopyButton
                          text={workItem.description}
                          getHtml={getDescriptionHtml}
                          tooltipLabel="Copy description"
                          data-testid="description-copy-button"
                        />
                      )}
                    </div>

                    <ScrollableContent maxHeight={FIELD_MAX_HEIGHT} className="text-sm">
                      <div ref={descriptionMarkdownRef} data-testid="work-item-description-content">
                        <MarkdownViewer content={workItem.description} />
                      </div>
                    </ScrollableContent>

                    {workItem.technicalApproach?.trim() && (
                      <>
                        <hr className="my-8" />
                        <div className="flex items-center justify-between mb-4">
                          <h2 className="text-2xl font-medium text-foreground">Technical Approach</h2>
                          <CopyButton
                            text={workItem.technicalApproach}
                            getHtml={getTechnicalApproachHtml}
                            tooltipLabel="Copy technical approach"
                            data-testid="technical-approach-copy-button"
                            className="text-card-foreground"
                          />
                        </div>
                        <ScrollableContent maxHeight={FIELD_MAX_HEIGHT} className="text-sm">
                          <div ref={technicalApproachMarkdownRef} data-testid="work-item-technical-approach-content">
                            <MarkdownViewer content={workItem.technicalApproach} />
                          </div>
                        </ScrollableContent>
                      </>
                    )}
                  </Card>

                </>
              )}

              {activeTab === 'workflow-history' && (
                <Card className="p-8 mb-8">
                  <WorkflowHistoryTab
                    projectId={projectId}
                    workItemId={workItem.id}
                  />
                </Card>
              )}

              {activeTab === 'test-cases' && (
                <Card className="p-8 mb-8">
                  <TestCasesTab projectId={projectId} workItemId={workItem.id} onNavigate={() => onOpenChange(false)} />
                </Card>
              )}

              {activeTab === 'attachments' && (
                <Card className="p-8 mb-8">
                  <AttachmentsTab projectId={projectId} workItemId={workItem.id} canEdit={canEdit} />
                </Card>
              )}
            </div>
        </div>

        <TriggerWorkflowFromEntityDialog
          projectId={projectId}
          open={triggerPickerOpen}
          onOpenChange={setTriggerPickerOpen}
          onSelect={(def) => setTriggerDefinition(def)}
          linkedEntityType="WorkItem"
          linkedEntityId={workItem.id}
        />

        <TriggerWorkflowDialog
          projectId={projectId}
          definition={triggerDefinition}
          open={triggerDefinition !== null}
          onOpenChange={(o) => { if (!o) setTriggerDefinition(null) }}
          extraInputValues={{ workItemId: workItem.id }}
          linkedEntityId={workItem.id}
          linkedEntityType="WorkItem"
        />
      </DialogContent>
    </Dialog>
  )
}
